import React from 'react';
import { Box, Menu, MenuItem } from '@mui/material';
import { ApolloError } from '@apollo/client';
import { useTheme } from '@mui/material/styles';

import {
  IconButton,
  LinearProgress,
  LoadedContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  EqualsIcon,
  MinusIcon,
  MoreVertIcon,
  ProgressIndicator,
  Pagination,
} from '@occc/ui-design-system';
import { SteeringWalletFragment } from '@occc/graphql';
import { titleByBasis } from '@occc-app/routes/Wallet/constants';
import { IPaginationProps } from '@occc-app/hooks/usePaginatedQuery';

type WalletsTableProps = {
  wallets: SteeringWalletFragment[] | null | undefined;
  loading: boolean;
  error?: ApolloError;
  onViewDetails: (item: SteeringWalletFragment) => void;
  paginationProps: IPaginationProps;
};

export const MemberWalletsTable: React.FC<WalletsTableProps> = ({
  wallets,
  loading,
  error,
  onViewDetails,
  paginationProps,
}) => {
  const [actionsForUuid, setActionsForUuid] = React.useState<string | null>();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const getHandlerForAdditionalActions =
    (item: SteeringWalletFragment) => (event: React.MouseEvent<HTMLElement>) => {
      event.stopPropagation();

      setAnchorEl(event.currentTarget);
      setActionsForUuid(item.uuid);
    };

  const handleOptionsMenuClose = React.useCallback(() => {
    setAnchorEl(null);
    setActionsForUuid(null);
  }, []);

  const menuItemOnClick = (
    event: React.MouseEvent<HTMLLIElement, MouseEvent>,
    doAction: () => void,
  ) => {
    event.stopPropagation();
    doAction();
    handleOptionsMenuClose();
  };

  const renderMoreOptionsMenu = (item: SteeringWalletFragment) => {
    // TODO: menu with 3 dots is already used in multiple places and
    // it's a good candidate to be a separate component
    return (
      <Menu
        open={item.uuid === actionsForUuid}
        onClose={(event) => {
          (event as any).stopPropagation();
          handleOptionsMenuClose();
        }}
        anchorEl={anchorEl}
        PaperProps={{
          sx: {
            borderRadius: '4px',
          },
        }}
      >
        <MenuItem
          onClick={(event) =>
            menuItemOnClick(event, () => {
              onViewDetails(item);
            })
          }
        >
          View Details
        </MenuItem>
      </Menu>
    );
  };

  const theme = useTheme();

  const renderData = (items: SteeringWalletFragment[]) => {
    return (
      <>
        {loading && <LinearProgress />}
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Wallet</TableCell>

              <TableCell>
                <Box display='flex' flexDirection='row' justifyContent='center' alignItems='center'>
                  <Box>Wallet Capacity</Box>
                  <Box
                    ml='auto'
                    mr='auto'
                    display='flex'
                    justifyContent='center'
                    alignItems='center'
                  >
                    <MinusIcon />
                  </Box>
                </Box>
              </TableCell>

              <TableCell>
                <Box display='flex' flexDirection='row' justifyContent='center' alignItems='center'>
                  <Box>Share of Wallet</Box>
                  <Box
                    ml='auto'
                    mr='auto'
                    display='flex'
                    justifyContent='center'
                    alignItems='center'
                  >
                    <EqualsIcon />
                  </Box>
                </Box>
              </TableCell>

              <TableCell>Wallet Opportunity</TableCell>
              {/* table cell for opportunity indicator */}
              <TableCell />
              {/* table cell for three dots */}
              <TableCell />
            </TableRow>
          </TableHead>

          <TableBody>
            {items.map((item, index) => (
              <TableRow
                key={item.uuid}
                onClick={(event) => {
                  event.stopPropagation();
                  onViewDetails(item);
                }}
              >
                <TableCell>
                  <Box display='flex' flexDirection='row' alignItems='center'>
                    <Typography variant='subtitle2'>{item.name}</Typography>

                    {item.configuration.basis && (
                      <Box ml={0.5}>{`(${titleByBasis[item.configuration.basis]})`}</Box>
                    )}
                  </Box>
                </TableCell>

                <TableCell>{item.statistics.walletCapacity}</TableCell>
                <TableCell>{item.statistics.walletShare}</TableCell>

                <TableCell>
                  <Typography variant='subtitle2'>{item.statistics.walletOpportunity}</Typography>
                </TableCell>

                <TableCell>
                  {item.statistics.walletCapacity > 0 && (
                    <Box display='flex' justifyContent='center' alignItems='center'>
                      <ProgressIndicator
                        color={
                          theme.palette.WalletSteeringOpportunityIndicatorColors[
                            index % theme.palette.WalletSteeringOpportunityIndicatorColors.length
                          ]
                        }
                        current={item.statistics.walletShare ?? 0}
                        total={item.statistics.walletCapacity}
                      />
                    </Box>
                  )}
                </TableCell>

                <TableCell align='right'>
                  <IconButton onClick={getHandlerForAdditionalActions(item)} sx={{ p: 0 }}>
                    <MoreVertIcon />
                  </IconButton>
                  {renderMoreOptionsMenu(item)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        <Pagination {...paginationProps} />
      </>
    );
  };

  return (
    <LoadedContent<SteeringWalletFragment>
      error={error}
      loading={loading}
      items={wallets}
      renderData={renderData}
    />
  );
};
